import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';
import { buttonVariants } from '../button/button-variants';
import { Separator } from '../separator/separator';
import { buttonGroupVariants } from './button-group-variants';

export type ButtonGroupProps = ComponentProps<'fieldset'> & VariantProps<typeof buttonGroupVariants>;

// name is required: radios only exclude one another when they share one.
// `input` carries a numeric `size` attribute of its own, so it makes way for the button's size variant.
export type ButtonGroupRadioProps = Omit<ComponentProps<'input'>, 'type' | 'size'> & {
  name: string;
  size?: VariantProps<typeof buttonVariants>['size'];
};

const ButtonGroup = ({ className, orientation = 'horizontal', ...props }: ButtonGroupProps) => (
  <fieldset
    data-slot="button-group"
    data-orientation={orientation}
    className={cn(buttonGroupVariants({ orientation, className }))}
    {...props}
  />
);

const ButtonGroupText = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="button-group-text"
    className={cn(
      "flex items-center gap-2 rounded-md border border-current/15 px-2.5 font-medium text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
      className
    )}
    {...props}
  />
);

const ButtonGroupSeparator = ({ className, orientation = 'vertical', ...props }: ComponentProps<typeof Separator>) => (
  <Separator
    data-slot="button-group-separator"
    orientation={orientation}
    // The seam between two children is already drawn by the trailing border of the first, so the
    // separator only reserves the gap rather than painting a second line beside it.
    className={cn(
      'relative self-stretch bg-transparent',
      orientation === 'vertical' ? 'my-px h-auto w-0' : 'mx-px h-0 w-auto',
      className
    )}
    {...props}
  />
);

/**
 * A radio styled as a group button, for one choice among a few.
 * Give every choice in a group the same name, or each one becomes a group of one.
 * Disable them together with `disabled` on the ButtonGroup, which the fieldset applies to every control inside it.
 *
 * `className` styles the visible label rather than the input, which is only ever visually hidden.
 */
const ButtonGroupRadio = ({ className, children, size, ...props }: ButtonGroupRadioProps) => (
  <label
    data-slot="button"
    className={cn(
      buttonVariants({ variant: 'outline', size }),
      // Anchors the absolutely positioned input, or focusing it scrolls whichever positioned ancestor it lands in.
      'relative cursor-pointer',
      'transition-[color,background-color,box-shadow]',
      'has-checked:border-ring has-checked:bg-accent has-checked:text-accent-foreground has-checked:hover:bg-accent',
      'has-focus-visible:z-20',
      'has-focus-visible:border-ring has-focus-visible:ring-2 has-focus-visible:ring-ring/50',
      'has-disabled:pointer-events-none has-disabled:opacity-50',
      // Forced colours discard the checked background, which would leave the selection invisible.
      'forced-colors:has-checked:bg-[Highlight] forced-colors:has-checked:text-[HighlightText]',
      'forced-colors:has-checked:hover:bg-[Highlight]',
      className
    )}
  >
    <input className="sr-only" {...props} type="radio" />
    {children}
  </label>
);

export { ButtonGroup, ButtonGroupRadio, ButtonGroupSeparator, ButtonGroupText };
